import { OptionLayer } from 'apps/web/types/labelled-collection.type';
import { OptionType } from 'apps/web/types/option';

import {
  CategorizedComboboxOption,
  OptionItem,
} from './CategorizedCombobox/types';

import { CategorizedComboboxField, RemoveButton, RemoveButtonVariant } from '.';
interface ComboboxFieldPairProps {
  displaySelectedCount?: boolean;
  placeholderWord?: string;
  index: number;
  primaryLabel: string;
  secondaryLabel: string;
  primaryKey: string;
  secondaryKey: string;
  primaryOptions: OptionLayer[];
  secondaryOptions: OptionLayer[] | OptionType[] | OptionItem[];
  watchedValues: Record<string, unknown>;
  setValue: (
    key: string,
    value: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => void;
  onRemoveSection: (index: number) => void;
  errors?: Record<string, { message: string } | string>;
  triggerValidation?: (fieldName: string) => void;
  totalSections: number;
}

export default function ComboboxFieldPair({
  placeholderWord,
  index,
  primaryLabel,
  secondaryLabel,
  primaryKey,
  secondaryKey,
  primaryOptions,
  secondaryOptions,
  watchedValues,
  displaySelectedCount = true,
  setValue,
  onRemoveSection,
  errors = {},
  triggerValidation,
  totalSections,
}: ComboboxFieldPairProps) {
  const handleRemoveSection = () => {
    onRemoveSection(index);
  };

  const primaryError =
    typeof errors[primaryKey] === 'object'
      ? (errors[primaryKey] as { message: string }).message
      : errors[primaryKey];
  const secondaryError =
    typeof errors[secondaryKey] === 'object'
      ? (errors[secondaryKey] as { message: string }).message
      : errors[secondaryKey];

  const handlePrimaryChange = (
    value: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => {
    setValue(primaryKey, value);
    triggerValidation?.(primaryKey);
    triggerValidation?.(secondaryKey);
  };

  const handleSecondaryChange = (
    value: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => {
    setValue(secondaryKey, value);
    triggerValidation?.(secondaryKey);
    triggerValidation?.(primaryKey);
  };

  return (
    <div className="col-span-full grid grid-cols-2 gap-4">
      <div className="col-span-full flex w-full flex-col gap-4">
        {index === 0 && <label className="text-lg">{primaryLabel}</label>}
        <div className="flex w-full gap-2 md:flex-row md:gap-4 justify-center">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <CategorizedComboboxField
              multiSelect
              className="flex h-full w-full flex-col"
              displaySelectedCount={displaySelectedCount}
              error={!!primaryError}
              errorMessage={primaryError}
              id={primaryKey}
              maxSelections={1}
              options={primaryOptions}
              placeholder={`Select ${placeholderWord}`}
              value={watchedValues[primaryKey] as CategorizedComboboxOption[]}
              onChange={handlePrimaryChange}
            />
            <CategorizedComboboxField
              multiSelect
              className="flex h-full w-full flex-col"
              displaySelectedCount={displaySelectedCount}
              error={!!secondaryError}
              errorMessage={secondaryError}
              id={secondaryKey}
              maxSelections={1}
              options={secondaryOptions}
              placeholder={`Select ${secondaryLabel}`}
              value={watchedValues[secondaryKey] as CategorizedComboboxOption[]}
              onChange={handleSecondaryChange}
            />
          </div>
          {totalSections > 1 && (
            <RemoveButton
              className="!p-0 mt-auto mb-auto ml-2 sm:ml-auto h-fit self-end md:self-start"
              variant={RemoveButtonVariant.DEFAULT}
              onClick={handleRemoveSection}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export const handleRemovePair = (
  index: number,
  primaryKey: string,
  secondaryKey: string,
  sectionsCount: number,
  setValue: (
    key: string,
    value: CategorizedComboboxOption | CategorizedComboboxOption[],
  ) => void,
  watchedValues: Record<string, unknown>,
  setSectionsCount: (count: number) => void,
): void => {
  const keysToRemove = [
    index === 0 ? primaryKey : `${primaryKey}${index + 1}`,
    index === 0 ? secondaryKey : `${secondaryKey}${index + 1}`,
  ];
  keysToRemove.forEach((key) => setValue(key, []));

  Array.from({ length: sectionsCount - index - 1 }).forEach(
    (unused, offset) => {
      const i = index + offset + 1;
      const getKey = (base: string, idx: number) =>
        idx === 0 ? base : `${base}${idx + 1}`;

      const currentPrimaryKey = getKey(primaryKey, i);
      const currentSecondaryKey = getKey(secondaryKey, i);
      const nextPrimaryKey = getKey(primaryKey, i - 1);
      const nextSecondaryKey = getKey(secondaryKey, i - 1);

      setValue(
        nextPrimaryKey,
        (watchedValues[currentPrimaryKey] as CategorizedComboboxOption[]) || [],
      );
      setValue(
        nextSecondaryKey,
        (watchedValues[currentSecondaryKey] as CategorizedComboboxOption[]) ||
        [],
      );

      if (i === sectionsCount - 1) {
        setValue(currentPrimaryKey, []);
        setValue(currentSecondaryKey, []);
      }
    },
  );

  setSectionsCount(sectionsCount - 1);
};
